/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import { TaskAccordion } from '@/components/TaskAccordion';
import { NoteAccordion } from '@/components/cards/NoteAccordion';
import { EmailCard } from '@/components/cards/EmailCard';
import { PolicyLinkedCard } from '@/components/cards/PolicyLinkedCard';
import { useObjectContext } from '@/hooks/useObjectContext';
import { Box, Collapse, Typography } from '@mui/material';
import { LoadingActivityCard } from '@/components/cards/LoadingActivityCard';
import { setCount } from '@/features/engagements/engagementsSlice';
import { CreatedObjectCard } from '../cards/createdObjectCard';
import { SpecialActivityCard } from '../cards/SpecialActivityCard';
import {
	useGetAllActivitiesQuery,
	useListNotesQuery,
	useListTasksQuery,
} from '@/features/engagements/engagementsApi';
import { useParams } from 'react-router-dom';
import { useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import { MSG_TYPE } from '@/utils/folders';
import { NameChangeActivityCard } from '../cards/NameChangeCard';
import { safelyParseDate } from '@/utils/date';
import { getPrimaryObject } from '@/utils/getPrimaryObject';

export const ActivityTab = () => {
	const dispatch = useDispatch();
	const params = useParams();
	const isClient = Boolean(params?.clientId);
	const isDeal = Boolean(params?.dealId);
	const isRelationshipCompany = Boolean(params?.relationshipCompanyId);

	const { refreshIndicator } = useSelector((state) => state.attachmentsTable);

	const { dealRow, clientRow, relationshipCompany } = useObjectContext();

	const primaryObject = getPrimaryObject({
		isRelationshipCompany,
		isClient,
		relationshipCompany,
		client: clientRow,
		deal: dealRow,
	});
	const driveId = primaryObject?.driveId;
	const clientFolderDriveId = primaryObject?.clientFolderDriveId;

	const filesQuery = useGetFoldersAndFilesQuery(
		{
			itemId: driveId,
			driveId: clientFolderDriveId,
			recursive: true,
		},
		{ skip: !driveId || !clientFolderDriveId },
	);

	const msgFiles = useMemo(
		() =>
			(filesQuery.data?.files ?? []).filter(
				(f) => f?.file?.mimeType == MSG_TYPE || f?.file?.mimeType == 'message/rfc822',
			),
		[filesQuery.data?.files],
	);

	const filters = useMemo(() => {
		const objectFilter = isRelationshipCompany
			? `relationshipCompanyId := ${params?.relationshipCompanyId}`
			: isDeal
				? `dealId := ${params?.dealId}`
				: `clientId := ${params?.clientId}`;

		return `completed := true && ${objectFilter}`;
	}, [
		isDeal,
		isRelationshipCompany,
		params?.dealId,
		params?.clientId,
		params?.relationshipCompanyId,
	]);

	const tasksQuery = useListTasksQuery(
		{ filters },
		{
			skip: isRelationshipCompany
				? !params?.relationshipCompanyId
				: !params?.dealId && !params?.clientId,
		},
	);
	const tasks = tasksQuery.data ?? [];

	const noteFilters = isRelationshipCompany
		? { relationshipCompanyId: params?.relationshipCompanyId }
		: isDeal
			? { dealId: params?.dealId }
			: { clientId: params?.clientId };

	const {
		data: notesData,
		isError,
		isSuccess,
		...notesQuery
	} = useListNotesQuery(
		{ filters: noteFilters, limit: 200 },
		{
			skip: !Object.values(noteFilters).some(Boolean),
		},
	);

	const notes = notesData?.data ?? [];

	const { data: activitiesData, ...activitiesQuery } = useGetAllActivitiesQuery(
		{ clientId: params?.clientId, dealId: params?.dealId },
		{ skip: isRelationshipCompany || (!params?.dealId && !params?.clientId) },
	);
	const activities = activitiesData?.data ?? [];

	const loadingActivities =
		!isRelationshipCompany && !activitiesQuery.isError && !activitiesQuery.isSuccess;
	const loading =
		!isError &&
		!isSuccess &&
		!tasksQuery.isError &&
		!tasksQuery.isSuccess;

	useEffect(() => {
		if (filesQuery.data) {
			filesQuery.refetch();
		}
		if (notesData) {
			notesQuery.refetch();
		}
		if (tasksQuery.data) {
			tasksQuery.refetch();
		}
	}, [refreshIndicator]);

	const { count } = useSelector((state) => state.engagementsSlice);

	const activitiesCount = Math.max(count.activities ?? 0, 1);

	// const activitiesCount = Math.max(activities.length ?? 0, 1);
	const isLoading = loading || loadingActivities;

	const sortedActivities = useMemo(() => {
		const updatedActivities = (activities ?? []).map((a) => ({
			...a,
			lastUpdated: safelyParseDate(a.createdDate)?.getTime() ?? null,
		}));
		const sorted = [
			...notes.map((note) => ({
				type: 'note',
				data: note,
				lastUpdated: safelyParseDate(note.timestamp)?.getTime(),
			})),
			...tasks.map((task) => ({ type: 'task', data: task })),
			...(msgFiles ?? []).map((file) => {
				const sentTime =
					file.sortDate ??
					(file.sentDate ? safelyParseDate(file.sentDate)?.getTime() : null);
				return {
					type: 'file',
					data: {
						...file,
						lastUpdated:
							sentTime ??
							(file.createdDateTime
								? safelyParseDate(file.createdDateTime)?.getTime()
								: null),
					},
				};
			}),
			...updatedActivities.map((activity) => ({
				type: 'activity',
				data: activity,
			})),
		]
			.map((v) => ({
				...v,
				sortDate:
					v?.sortDate ?? v?.data?.sortDate ?? v?.lastUpdated ?? v?.data?.lastUpdated,
			}))
			.sort((a, b) => (b.sortDate ?? 0) - (a.sortDate ?? 0));
		return sorted;
	}, [msgFiles, notes, tasks, activities]);

	useEffect(() => {
		if (sortedActivities && dispatch) {
			dispatch(setCount({ key: 'activities', count: sortedActivities.length }));
		}
	}, [sortedActivities, dispatch]);

	const renderActivityItem = (activity, index) => {
		const specialActivityTypes = ['prospectToClient', 'clientToProspect'];
		switch (activity.type) {
			case 'note':
				return <NoteAccordion key={`activity-note-${index}`} note={activity.data} />;
			case 'task':
				return (
					<TaskAccordion
						key={`activity-task-${index}`}
						task={activity.data}
						index={index}
					/>
				);
			case 'file':
				return <EmailCard key={`msg-file-${index}`} email={activity} />;
			case 'activity':
				if (activity.data.activityType.toUpperCase().trim() == 'POLICIES LINKED') {
					//Filter out old actiivities that do not have a policyTransactionId
					if (!activity.data.policyTransactionId) {
						return null;
					}
					return (
						<PolicyLinkedCard
							key={`activity-linked-card-${index}`}
							activity={activity}
						/>
					);
				} else if (activity.data.activityType.toUpperCase().trim() == 'CLIENTNAMECHANGE') {
					return (
						<NameChangeActivityCard
							key={`name-change-activity-${index}`}
							activity={activity}
						/>
					);
				} else if (specialActivityTypes.includes(activity.data.activityType)) {
					return (
						<SpecialActivityCard
							key={`special-activity-${index}`}
							activity={activity.data}
						/>
					);
				} else {
					return (
						<CreatedObjectCard
							key={`object-created-card-${index}`}
							activity={activity.data}
						/>
					);
				}

			default:
				return null;
		}
	};

	return (
		<Box px={'0.5em'}>
			{isLoading ? (
				[...Array(activitiesCount)].map((_, i) => (
					<LoadingActivityCard key={`loading-activity-${i}`} />
				))
			) : sortedActivities.length > 0 ? (
				<TransitionGroup>
					{sortedActivities.map((activity, index) => (
						<Collapse sx={{ marginBottom: '5px' }} key={`activity-collapse-${index}`}>
							{renderActivityItem(activity, index)}
						</Collapse>
					))}
				</TransitionGroup>
			) : (
				<Typography textAlign={'center'}>No activity logged yet.</Typography>
			)}
		</Box>
	);
};
ActivityTab.displayName = 'ActivityTab';
